/**
 * TeamThirdsPitch (EPIC-110 D2): "Pases por tercio" — the pitch split into
 * three vertical bands coloured by the share of the team's located `Pass`
 * events whose origin falls in each third (oriented to the team's attacking
 * direction, so away is mirrored). Percentages come from
 * `computeTeamControlMap` applied to the team's located passes only.
 *
 * Band labels are plain HTML laid out in DISPLAY order (reversed when the
 * pitch is rotated) so the text never renders upside down.
 */
import { useMemo, type FC } from "react";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Team } from "../../types";
import {
  computeTeamControlMap,
  type TeamControlThirds,
  type TeamSide,
} from "../../utils/teamControlMap";
import { PITCH_WIDTH, PITCH_HEIGHT } from "../../utils/heatMapZones";
import AttackDirectionCaption from "../atoms/AttackDirectionCaption";
import { CrestDirectionCaption, ROW_PITCH_ASPECT } from "./pitchRowLayout";
import PitchArrowsOverlay from "../atoms/PitchArrowsOverlay";
import { bandAlpha, bandLabel } from "./thirdsBandContrast";

interface TeamThirdsPitchProps {
  team: Team;
  side: TeamSide;
  events: MatchEvent[];
  /** Team colour (hex) used to tint the bands. */
  color: string;
  t: (key: string, fallback?: string) => string;
  rotated?: boolean;
  /** EPIC-111 D4: team-row column (no own border/padding/title, shared pitch
   * box, small crest + direction caption). */
  rowLayout?: boolean;
  "data-testid"?: string;
}

type ThirdKey = keyof TeamControlThirds;

function tint(hex: string, alpha: number): string {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return `rgba(100,116,139,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${alpha})`;
}

const TeamThirdsPitch: FC<TeamThirdsPitchProps> = ({
  team,
  side,
  events,
  color,
  t,
  rotated = false,
  rowLayout = false,
  "data-testid": testId,
}) => {
  const data = useMemo(
    () =>
      computeTeamControlMap(
        events.filter((ev) => ev.type === "Pass"),
        team.id,
        side,
      ),
    [events, team.id, side],
  );
  const sub = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);
  const label = team.short_name || team.name;

  // Canonical (x ascending) band order: home attacks +x so safe is on the left.
  const canonical: ThirdKey[] =
    side === "home"
      ? ["safe", "middle", "danger"]
      : ["danger", "middle", "safe"];
  const display = rotated ? [...canonical].reverse() : canonical;
  const bandW = PITCH_WIDTH / 3;
  const names: Record<ThirdKey, string> = {
    safe: t("analytics.controlMapSafeZone", "Safe zone"),
    middle: t("analytics.controlMapMiddleZone", "Middle zone"),
    danger: t("analytics.controlMapDangerZone", "Dangerous attack"),
  };

  return (
    <div
      className={
        rowLayout
          ? "flex flex-col items-center gap-2"
          : "flex flex-col items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/40 p-2"
      }
      data-testid={testId}
    >
      {!rowLayout && (
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-200">
          {t("analytics.thirdsPitchTitle", "Passes by third")}
        </h4>
      )}
      {data.total === 0 ? (
        <div
          className={`flex w-full items-center justify-center rounded-lg border border-dashed border-slate-700 px-4 text-center text-xs text-slate-400 ${
            rowLayout ? "" : "h-40"
          }`}
          style={rowLayout ? { aspectRatio: ROW_PITCH_ASPECT } : undefined}
          data-testid={sub("empty")}
        >
          {t("analytics.thirdsPitchEmpty", "No located passes")}
        </div>
      ) : (
        <>
          <div
            className="relative w-full rounded-lg border border-slate-700 bg-green-800 overflow-hidden"
            style={{ aspectRatio: `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}` }}
          >
            <div
              className="absolute inset-0"
              style={{ transform: rotated ? "rotate(180deg)" : undefined }}
              data-rotated={rotated ? "true" : undefined}
            >
              <svg
                viewBox={`-2 -2 ${PITCH_WIDTH + 4} ${PITCH_HEIGHT + 4}`}
                className="absolute inset-0 h-full w-full"
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={label}
                data-testid={sub("svg")}
              >
                <title>{label}</title>
                <rect
                  x={0}
                  y={0}
                  width={PITCH_WIDTH}
                  height={PITCH_HEIGHT}
                  fill="#2d6a2e"
                  rx={1}
                />
                {canonical.map((key, i) => (
                  <rect
                    key={key}
                    x={i * bandW}
                    y={0}
                    width={bandW}
                    height={PITCH_HEIGHT}
                    fill={tint(color, bandAlpha(data.thirds[key]))}
                    data-band={key}
                  />
                ))}
                <g
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth={0.4}
                  fill="none"
                >
                  <rect
                    x={0}
                    y={0}
                    width={PITCH_WIDTH}
                    height={PITCH_HEIGHT}
                    rx={0.5}
                  />
                  <line x1={bandW} y1={0} x2={bandW} y2={PITCH_HEIGHT} />
                  <line
                    x1={2 * bandW}
                    y1={0}
                    x2={2 * bandW}
                    y2={PITCH_HEIGHT}
                  />
                </g>
              </svg>
              <PitchArrowsOverlay
                side={side}
                reversed={rotated}
                lanes="outer"
                data-testid={sub("pitch-arrows")}
              />
            </div>
            <div className="absolute inset-0 flex">
              {display.map((key) => {
                const dark = bandLabel(color, data.thirds[key]).tone === "dark";
                const tone = dark ? "text-black" : "text-white drop-shadow";
                return (
                  <div
                    key={key}
                    className="flex min-w-0 flex-1 flex-col items-center justify-center px-1.5 text-center"
                    data-testid={sub(key)}
                    data-value={data.thirds[key]}
                  >
                    <span
                      className={`text-lg font-bold ${tone}`}
                    >{`${data.thirds[key]}%`}</span>
                    <span
                      className={`break-words text-xs font-medium leading-tight ${tone}`}
                    >
                      {names[key]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          {!rowLayout && (
            <AttackDirectionCaption
              side={side}
              t={t}
              reversed={rotated}
              data-testid={testId}
            />
          )}
        </>
      )}
      {rowLayout && (
        <CrestDirectionCaption
          team={team}
          side={side}
          t={t}
          rotated={rotated}
          captionTestId={testId}
          crestTestId={sub("direction-crest")}
        />
      )}
    </div>
  );
};

export default TeamThirdsPitch;
